/**
 * DEV-1164 / DEV-1157 Phase 2g — iter 2 (2026-05-31 mockup design lock).
 *
 * Read-only Recurring Duties panel for the Engagements role-detail right
 * pane. Renders a year-ribbon overview at the top (perpetual entities
 * only) and cadence cards below. Each task row opens a side tray with
 * full detail (description, cadence detail, lead time, due window).
 *
 * Iter-2 changes from the original DEV-1164:
 *   * Section label "Recurring duties" (was "Rhythm")
 *   * Drop the rhythm description rendering at the top
 *   * Year ribbon SVG (5 cadence rows × 12 months) at top of panel
 *   * Cadence cards instead of inline cadence-grouped list
 *   * Click task name → side tray (RhythmTaskTray)
 *   * Conditional: hide entire panel when zero tasks
 *   * Conditional: hide ribbon for bounded entities (project/workspace);
 *     ribbon's calendar-year axis doesn't fit a finite-duration entity
 *
 * The mockup design session locked these decisions; see DEV-1164
 * 2026-05-31 comment for the full lock.
 */

import { useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiRoleRhythmDetailsForDefinition } from "../../api/useApiRoleRhythmDetailsForDefinition";
import { useApiSlotRhythmInstances } from "../../api/useApiSlotRhythmInstances";
import type { RhythmTaskDetail, SlotRhythmInstanceResponse } from "../../api/roleRhythms";
import { RhythmTaskTray } from "./RhythmTaskTray";
import styles from "./RhythmDisplayPanel.module.css";

interface RhythmDisplayPanelProps {
  /**
   * Slot's sourceDefinitionGuid; the panel resolves the attached rhythm
   * via the by-definition shortcut endpoint. Null/undefined = render nothing.
   */
  sourceDefinitionGuid: string | null | undefined;

  /**
   * DEV-1164 iter 2. When true, the entity is "perpetual" (geo or org
   * community) and the year ribbon makes sense. When false (bounded
   * entity — full project, mini-project, workspace) the ribbon is
   * suppressed because the calendar-year axis doesn't apply.
   *
   * Default: false (safer; cards-only render).
   */
  entityIsPerpetual?: boolean;

  /**
   * DEV-1224 (2026-06-03). When provided, the panel fetches the slot's
   * materialized rhythm task instances for the current calendar year
   * and overlays per-dot status glyphs on past cadence dots (completed,
   * skipped, overdue). When null/undefined, ribbon dots are synthesized
   * from cadence type alone (legacy behavior) — useful for surfaces
   * that don't have a slot anchor yet.
   */
  slotGuid?: string | null;
}

/**
 * Cadence display order — most frequent → least frequent.
 * Standing duties are handled in a separate section.
 */
const CADENCE_ORDER: string[] = [
  "daily",
  "weekly",
  "biweekly",
  "monthly_dom",
  "monthly_dow",
  "quarterly",
  "semiannual",
  "annual",
  "school_term",
  "fiscal_year_end",
];

/**
 * For year-ribbon plotting: which row in the SVG should each cadence
 * type land on, and how many marks should it produce across the year.
 * Returns dots positioned roughly when the cadence fires. Anchor JSON
 * parsing is intentionally simple — this is a sense-making visualization,
 * not a scheduler. More precise anchor-aware positioning can come later.
 */
function ribbonMarksForCadence(
  cadenceType: string | null,
): { row: number; positions: number[] } | null {
  if (!cadenceType) return null;
  switch (cadenceType) {
    case "annual":
      return { row: 0, positions: [0.5] };
    case "semiannual":
      return { row: 1, positions: [0.25, 0.75] };
    case "quarterly":
      return { row: 2, positions: [0.125, 0.375, 0.625, 0.875] };
    case "monthly_dom":
    case "monthly_dow":
      return {
        row: 3,
        positions: Array.from({ length: 12 }, (_, i) => (i + 0.5) / 12),
      };
    case "weekly":
      return {
        row: 4,
        positions: Array.from({ length: 52 }, (_, i) => (i + 0.5) / 52),
      };
    case "biweekly":
      return {
        row: 4,
        positions: Array.from({ length: 26 }, (_, i) => (i + 0.5) / 26),
      };
    case "daily":
      return {
        row: 4,
        positions: Array.from({ length: 60 }, (_, i) => (i + 0.5) / 60),
      };
    case "school_term":
      return { row: 0, positions: [0.7, 0.95] };
    case "fiscal_year_end":
      return { row: 0, positions: [0.5] };
    default:
      return null;
  }
}

const RIBBON_ROW_LABELS = ["Annual", "Semi", "Quarter", "Monthly", "Weekly"];

/**
 * Per-cadence-row fill color. Tinted to match the panel's existing
 * chip colors (set in module CSS), but defined inline because SVG
 * doesn't inherit CSS-variable fills inside <circle>.
 */
const RIBBON_ROW_FILL: string[] = [
  "#de8a6c", // Annual
  "#d7a55a", // Semi
  "#d7a55a", // Quarter
  "#6dccaa", // Monthly
  "#8ec0ee", // Weekly
];

const RIBBON_ROW_RADIUS = [6, 6, 5, 4, 3];

/**
 * DEV-1233 (2026-06-04). Day-of-year (0-indexed) for a calendar month
 * and day. Uses a fixed reference year — calendar position doesn't shift
 * meaningfully year-to-year for ribbon placement (leap-day drift of 1/365
 * is well below dot radius).
 */
function dayOfYearForMonthDay(month: number, day: number): number {
  const ref = new Date(2026, month - 1, day);
  const start = new Date(2026, 0, 1);
  return Math.floor((ref.getTime() - start.getTime()) / 86400000);
}

function positionFromMonthDay(month: number, day: number): number {
  return dayOfYearForMonthDay(month, day) / 365;
}

/**
 * DEV-1233. Anchor-aware synthesized positions for a task. Reads the
 * task's cadence_anchor JSON and computes positions on the calendar
 * rather than at hardcoded evenly-spaced fractions of the year. Returns
 * null when the anchor is missing or unparseable; caller falls back to
 * the hardcoded ribbonMarksForCadence positions.
 *
 * Anchor JSON shapes (see migration 195 for the canonical Rotary
 * Secretary seed):
 *   - annual / fiscal_year_end: {"month": M, "day_of_month": D}
 *   - semiannual: {"months": [M1, M2], "day_of_month": D}
 *   - quarterly: {"month_of_quarter": 1|2|3, "day_of_month": D}
 *   - monthly_dom: {"day_of_month": D}
 *   - monthly_dow: {"weekday": W, "week_in_month": N} — TODO; falls
 *     back for now (approximation is harder than for day-of-month)
 *   - weekly / biweekly / daily / school_term: hardcoded positions are
 *     fine (no calendar anchoring problem to fix).
 *
 * Position ordering MUST match `bucketIdxFromDate` so a real instance's
 * bucket aligns with the synth array index — e.g., quarterly moq=2
 * produces positions for Feb/May/Aug/Nov, and a real instance in May
 * (bucket index 1) maps to positions[1] which is also May.
 */
/**
 * DEV-1233 follow-up (2026-06-04). Render a cadence_anchor JSON blob as
 * human-readable text — surfaces "when this fires" beside each task
 * name in the cadence cards. Skip's feedback: knowing a task is
 * "monthly" tells you nothing about when in the month it fires; the
 * anchor data IS in the API response, just not surfaced.
 *
 * Returns "" when the anchor is missing or doesn't have a sensible
 * human form (caller renders nothing in that case).
 */
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const WEEKDAY_NAMES = [
  "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat",
];
function ordinal(n: number): string {
  if (n >= 11 && n <= 13) return `${n}th`;
  const last = n % 10;
  if (last === 1) return `${n}st`;
  if (last === 2) return `${n}nd`;
  if (last === 3) return `${n}rd`;
  return `${n}th`;
}
function formatCadenceAnchor(
  cadenceType: string,
  anchorJson: string | null | undefined,
): string {
  if (!anchorJson) return "";
  let anchor: unknown;
  try {
    anchor = JSON.parse(anchorJson);
  } catch {
    return "";
  }
  if (anchor === null || typeof anchor !== "object") return "";
  const a = anchor as Record<string, unknown>;

  if (cadenceType === "annual" || cadenceType === "fiscal_year_end") {
    const month = typeof a.month === "number" ? a.month : null;
    const day = typeof a.day_of_month === "number" ? a.day_of_month : null;
    if (month === null || month < 1 || month > 12) return "";
    if (day === null) return `${MONTH_NAMES[month - 1]}`;
    return `${MONTH_NAMES[month - 1]} ${day}`;
  }

  if (cadenceType === "semiannual") {
    const months = Array.isArray(a.months) ? (a.months as unknown[]) : [];
    const day = typeof a.day_of_month === "number" ? a.day_of_month : null;
    const ms = months
      .filter((m): m is number => typeof m === "number" && m >= 1 && m <= 12)
      .map((m) => MONTH_NAMES[m - 1]);
    if (ms.length === 0) return "";
    if (day === null) return ms.join(" + ");
    return ms.map((mn) => `${mn} ${day}`).join(" + ");
  }

  if (cadenceType === "quarterly") {
    const moq = typeof a.month_of_quarter === "number" ? a.month_of_quarter : null;
    const day = typeof a.day_of_month === "number" ? a.day_of_month : null;
    if (moq === null || moq < 1 || moq > 3) return "";
    const moqLabel = moq === 1 ? "1st" : moq === 2 ? "2nd" : "3rd";
    return day === null
      ? `${moqLabel} month of quarter`
      : `${ordinal(day)} of ${moqLabel} month`;
  }

  if (cadenceType === "monthly_dom") {
    const day = typeof a.day_of_month === "number" ? a.day_of_month : null;
    if (day === null) return "";
    return `${ordinal(day)} of each month`;
  }

  if (cadenceType === "monthly_dow") {
    const weekday = typeof a.weekday === "number" ? a.weekday : null;
    const wim = typeof a.week_in_month === "number" ? a.week_in_month : null;
    if (weekday === null || weekday < 0 || weekday > 6) return "";
    const wn = WEEKDAY_NAMES[weekday];
    if (wim === null) return `${wn} weekly in each month`;
    return `${ordinal(wim)} ${wn} of each month`;
  }

  if (cadenceType === "weekly" || cadenceType === "biweekly") {
    // Engine convention: 1 = Monday (per migration 195). Map to JS
    // Sunday-based array for friendly display.
    const dayNum = typeof a.day_of_week === "number" ? a.day_of_week : null;
    if (dayNum === null) return "";
    // Convert "1=Monday" → index 1 in Sunday-based array (Sun=0, Mon=1).
    const jsIdx = dayNum % 7;
    const base = cadenceType === "weekly" ? "Every" : "Every other";
    return `${base} ${WEEKDAY_NAMES[jsIdx]}`;
  }

  return "";
}

function synthPositionsFromAnchor(
  cadenceType: string,
  anchorJson: string | null | undefined,
): number[] | null {
  if (!anchorJson) return null;
  let anchor: unknown;
  try {
    anchor = JSON.parse(anchorJson);
  } catch {
    return null;
  }
  if (anchor === null || typeof anchor !== "object") return null;
  const a = anchor as Record<string, unknown>;

  if (cadenceType === "annual" || cadenceType === "fiscal_year_end") {
    const month = typeof a.month === "number" ? a.month : null;
    const day = typeof a.day_of_month === "number" ? a.day_of_month : 15;
    if (month === null || month < 1 || month > 12) return null;
    return [positionFromMonthDay(month, day)];
  }

  if (cadenceType === "semiannual") {
    const months = Array.isArray(a.months) ? (a.months as unknown[]) : [];
    const day = typeof a.day_of_month === "number" ? a.day_of_month : 1;
    if (months.length !== 2) return null;
    const m1 = typeof months[0] === "number" ? (months[0] as number) : null;
    const m2 = typeof months[1] === "number" ? (months[1] as number) : null;
    if (m1 === null || m2 === null) return null;
    // Sorted so positions[0] is always the earlier month — matches
    // bucketIdxFromDate which maps months 1-6 → bucket 0, 7-12 → bucket 1.
    const sorted = m1 <= m2 ? [m1, m2] : [m2, m1];
    return sorted.map((m) => positionFromMonthDay(m, day));
  }

  if (cadenceType === "quarterly") {
    const moq = typeof a.month_of_quarter === "number" ? a.month_of_quarter : null;
    const day = typeof a.day_of_month === "number" ? a.day_of_month : 15;
    if (moq === null || moq < 1 || moq > 3) return null;
    // moq=1 → Jan/Apr/Jul/Oct; moq=2 → Feb/May/Aug/Nov; moq=3 → Mar/Jun/Sep/Dec.
    // bucketIdxFromDate maps Math.floor(month/3) so Jan→0, Feb→0, Mar→0, Apr→1, etc.
    // For moq=1: month 1→bucket 0, month 4→bucket 1, month 7→bucket 2, month 10→bucket 3 ✓
    // For moq=2: month 2→bucket 0, month 5→bucket 1, month 8→bucket 2, month 11→bucket 3 ✓
    // For moq=3: month 3→bucket 0, month 6→bucket 1, month 9→bucket 2, month 12→bucket 3 ✓
    return [moq, moq + 3, moq + 6, moq + 9].map((m) => positionFromMonthDay(m, day));
  }

  if (cadenceType === "monthly_dom") {
    const day = typeof a.day_of_month === "number" ? a.day_of_month : 1;
    return Array.from({ length: 12 }, (_, i) => positionFromMonthDay(i + 1, day));
  }

  // monthly_dow / weekly / biweekly / daily / school_term — no anchor-based
  // override yet; caller falls back to hardcoded evenly-spaced positions.
  return null;
}

export function RhythmDisplayPanel({
  sourceDefinitionGuid,
  entityIsPerpetual = false,
  slotGuid = null,
}: RhythmDisplayPanelProps) {
  const { t } = useAppTranslation("pages");
  const { details, loading, source } =
    useApiRoleRhythmDetailsForDefinition(sourceDefinitionGuid);
  // DEV-1224 — instance history fetch (only fires when slotGuid is
  // provided). Returns empty array when the slot has no materialized
  // instances yet for the year; that's the natural fallback to the
  // synthesized-dots path below.
  const { instances: slotInstances } = useApiSlotRhythmInstances(slotGuid);
  const [trayTask, setTrayTask] = useState<RhythmTaskDetail | null>(null);
  // DEV-1257-followup. Custom tooltip for year-ribbon dots — native <title>
  // tooltips have ~700ms delay and use OS default styling (small text, no
  // theming). Tracking hoveredDot in state lets us render a foreignObject
  // tooltip that appears instantly with the theme font size + colors. The
  // <title> element stays for screen-reader accessibility (NVDA / VoiceOver).
  const [hoveredDotIdx, setHoveredDotIdx] = useState<number | null>(null);

  // No rhythm to show — render nothing.
  if (!sourceDefinitionGuid || source === "idle") return null;
  if (source === "empty" && !details && !loading) return null;

  if (loading) {
    return (
      <div className={styles.detailSection}>
        <div className={styles.sectionLabel}>
          {t("engagements.rhythmSectionLabel")}
        </div>
        <div className={styles.placeholder}>
          {t("engagements.rhythmLoading")}
        </div>
      </div>
    );
  }

  if (!details) return null;

  const recurring = details.tasks.filter(
    (task) => task.taskKind === "recurring_task",
  );
  const standing = details.tasks.filter(
    (task) => task.taskKind === "standing_duty",
  );

  // Conditional render: hide entire panel when the rhythm has no tasks
  // at all (would otherwise show an empty section header).
  if (recurring.length === 0 && standing.length === 0) return null;

  // Group recurring by cadence for the card stack.
  const byCadence = new Map<string, RhythmTaskDetail[]>();
  for (const task of recurring) {
    const key = task.cadenceType ?? "_unknown";
    const bucket = byCadence.get(key) ?? [];
    bucket.push(task);
    byCadence.set(key, bucket);
  }
  const orderedCadenceKeys: string[] = [];
  for (const c of CADENCE_ORDER) {
    if (byCadence.has(c)) orderedCadenceKeys.push(c);
  }
  for (const k of byCadence.keys()) {
    if (!orderedCadenceKeys.includes(k)) orderedCadenceKeys.push(k);
  }

  // Resolve the cadence-card label (e.g., "Weekly") via i18n. Hoisted
  // above the ribbon-dots construction so dot tooltips can include the
  // human cadence name.
  const cadenceLabel = (cadenceType: string): string => {
    const key = `engagements.rhythmCadence.${cadenceType}`;
    const localized = t(key);
    return localized === key ? cadenceType : localized;
  };

  // Year-ribbon plot data — only for perpetual entities with at least
  // one recurring task. Bounded entities (projects/workspaces) suppress
  // the ribbon entirely; their calendar-year axis doesn't fit a finite
  // entity duration.
  const showRibbon = entityIsPerpetual && recurring.length > 0;
  // DEV-1164 follow-up (2026-06-01): vertical "today" indicator. Position
  // computed as fraction of year. Used for both the ribbon line and the
  // cadence-card stack reference (visual continuity).
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const yearEnd = new Date(now.getFullYear() + 1, 0, 1);
  const todayPosition =
    (now.getTime() - yearStart.getTime()) /
    (yearEnd.getTime() - yearStart.getTime());
  // Friendly month label for a position-in-year fraction (0..1). Used in
  // ribbon dot tooltips so admin can see roughly when the mark fires.
  const monthForPosition = (p: number): string => {
    const day = Math.max(0, Math.min(364, Math.round(p * 365)));
    const d = new Date(now.getFullYear(), 0, 1 + day);
    return d.toLocaleString(undefined, { month: "short" });
  };
  // DEV-1224 follow-up (2026-06-03): integrated visual scheme.
  // Composes a synthesized base layer (full year cadence shape) with
  // real instance overlays. Past synthesized positions with no real
  // instance render as muted grey "gap" dots; completed instances
  // render as hollow rings; skipped/overdue keep their status marks.
  //
  // The stroke/strokeWidth fields support the hollow-ring treatment
  // for completed instances (no fill, only an outline in the cadence
  // row color). Future/pending dots use a solid fill with no stroke.
  type RibbonDot = {
    x: number;
    y: number;
    r: number;
    fill: string;
    stroke?: string;
    strokeWidth?: number;
    opacity: number;
    label: string;
    /**
     * 'skipped' | 'overdue' | null. When non-null, the SVG draws a
     * small status marker centered on the dot. Completed dots
     * communicate via the hollow-ring shape itself; no glyph needed
     * (and tiny glyphs were illegible on small screens — Skip
     * 2026-06-03 feedback).
     */
    statusMark: "skipped" | "overdue" | null;
    /**
     * DEV-1257-followup. Back-reference to the task this dot
     * represents. Click handler in the SVG render opens the
     * RhythmTaskTray for this task. Per the design lock with Skip
     * (Q=a), future/past/current dots are all clickable.
     */
    task: RhythmTaskDetail;
  };
  const dotsWithStatus: RibbonDot[] = [];

  // DEV-1224 — instances grouped by task_guid for the per-task lookup
  // below. When a task has materialized instances within the year,
  // its dots come from REAL period_anchors with status; when it
  // doesn't (engine hasn't materialized yet, or no slot context),
  // the synthesized fallback positions are used (legacy behavior).
  const instancesByTask = new Map<string, SlotRhythmInstanceResponse[]>();
  for (const inst of slotInstances) {
    const list = instancesByTask.get(inst.taskGuid) ?? [];
    list.push(inst);
    instancesByTask.set(inst.taskGuid, list);
  }

  const yearStartMs = yearStart.getTime();
  const yearEndMs = yearEnd.getTime();
  const yearSpanMs = yearEndMs - yearStartMs;
  const nowMs = now.getTime();

  const positionForIso = (iso: string): number | null => {
    const ts = Date.parse(iso);
    if (Number.isNaN(ts) || yearSpanMs <= 0) return null;
    return (ts - yearStartMs) / yearSpanMs;
  };

  // Per-status visual treatment. Inverted visual hierarchy (Skip
  // 2026-06-03 design lock): what's OPEN/UPCOMING is loud, what's
  // TERMINAL is muted/hollow. The eye naturally scans for what needs
  // attention instead of what's already done.
  //
  // - completed: hollow ring in cadence-row color (no fill, just outline).
  //   The hollow shape IS the signal; no glyph required (tiny check
  //   marks were illegible on small screens — Skip 2026-06-03 feedback).
  // - skipped: ghosted grey solid dot with small white X.
  // - overdue: solid red with outer ring (loudest signal — needs action).
  // - pending/due/snoozed in the past: defensive overdue render so the
  //   user sees attention-worthy items the engine hasn't promoted.
  // - pending future: full cadence-row color (solid) — the default.
  const statusVisualForInstance = (
    inst: SlotRhythmInstanceResponse,
    fallbackFill: string,
  ): {
    fill: string;
    stroke?: string;
    strokeWidth?: number;
    opacity: number;
    statusMark: "skipped" | "overdue" | null;
  } => {
    if (inst.status === "completed") {
      return {
        fill: "transparent",
        stroke: fallbackFill,
        strokeWidth: 1.5,
        opacity: 1,
        statusMark: null,
      };
    }
    if (inst.status === "skipped") {
      return { fill: "var(--text-muted)", opacity: 0.45, statusMark: "skipped" };
    }
    if (inst.status === "overdue") {
      return { fill: "var(--color-error)", opacity: 1, statusMark: "overdue" };
    }
    const ts = Date.parse(inst.periodAnchor);
    const isPast = !Number.isNaN(ts) && ts <= nowMs;
    if (
      isPast
      && (inst.status === "due" || inst.status === "pending" || inst.status === "snoozed")
    ) {
      return { fill: "var(--color-error)", opacity: 1, statusMark: "overdue" };
    }
    return { fill: fallbackFill, opacity: 1, statusMark: null };
  };

  const labelForInstance = (
    inst: SlotRhythmInstanceResponse,
    task: RhythmTaskDetail,
  ): string => {
    const cadName = task.cadenceType ? cadenceLabel(task.cadenceType) : "";
    const pos = positionForIso(inst.periodAnchor) ?? 0;
    const month = monthForPosition(pos);
    const statusWord = t(
      `engagements.rhythmInstanceStatus.${inst.status}`,
    );
    // i18n graceful fallback: when a status key is missing, use raw status string.
    const statusKey = `engagements.rhythmInstanceStatus.${inst.status}`;
    const statusText = statusWord === statusKey ? inst.status : statusWord;
    let line = cadName
      ? `${task.name} — ${cadName} (${month}) · ${statusText}`
      : `${task.name} (${month}) · ${statusText}`;
    if (inst.status === "completed" && inst.completedByDisplayName) {
      line += ` — ${inst.completedByDisplayName}`;
    }
    if (inst.status === "skipped" && inst.skipReason) {
      line += ` — ${inst.skipReason}`;
    }
    return line;
  };

  if (showRibbon) {
    const X_START = 80;
    const X_END = 860;
    const X_RANGE = X_END - X_START;
    const Y_ROWS = [16, 46, 76, 106, 136]; // Annual / Semi / Quarter / Monthly / Weekly

    // DEV-1224 follow-up (2026-06-03): two-pass composition. The
    // synthesized cadence shape forms the BASE LAYER so the whole year
    // stays readable as "year at a glance" (Skip 2026-06-03 design
    // lock). Real materialized instances OVERLAY their actual
    // period_anchor positions with status visuals. Synthesized
    // positions that no real instance covers fall back to either:
    //   - muted grey "gap" dot when the position is past today (no
    //     record exists for a period that should have happened)
    //   - full cadence-row color when the position is in the future
    //     (upcoming work — the original ribbon treatment).
    for (const task of recurring) {
      const marks = ribbonMarksForCadence(task.cadenceType ?? null);
      if (!marks) continue;
      const cadName = task.cadenceType ? cadenceLabel(task.cadenceType) : "";
      const taskInstances = instancesByTask.get(task.taskGuid) ?? [];
      const baseY = Y_ROWS[marks.row];
      const baseR = RIBBON_ROW_RADIUS[marks.row];
      const baseFill = RIBBON_ROW_FILL[marks.row];

      // DEV-1233 (2026-06-04): prefer anchor-derived positions when the
      // task's cadence_anchor JSON specifies a calendar date. Falls back
      // to the hardcoded evenly-spaced positions from
      // ribbonMarksForCadence when the anchor is missing/unparseable or
      // the cadence type doesn't have a calendar-anchor concept
      // (weekly/biweekly/daily). Pre-fix, an annual task with anchor
      // {"month": 12} rendered at mid-year (the hardcoded 0.5 default)
      // instead of December.
      const anchorPositions = synthPositionsFromAnchor(
        task.cadenceType ?? "",
        task.cadenceAnchor,
      );
      const synthPositions = anchorPositions ?? marks.positions;

      // Cadence-aware match between a real period_anchor date and its
      // synthesized position index. Position-distance matching breaks
      // for monthly_dom tasks anchored on the 1st of the month (Jul 1
      // is closer to Jun mid-point than Jul mid-point), so we use
      // calendar bucketing for cadences with a natural calendar period.
      // Weekly/biweekly/daily have no natural calendar bucket; they
      // fall back to position-distance with a wide tolerance.
      //
      // DEV-1224 follow-up 2026-06-03: parse the ISO YYYY-MM-DD string
      // as a LOCAL-time date, not via `new Date(iso)` (which treats
      // bare ISO dates as UTC midnight). In negative-offset timezones
      // (e.g., EDT), UTC midnight Sep 1 maps to local Aug 31 8 PM, so
      // `.getMonth()` returns 7 (Aug) instead of 8 (Sep). That shifted
      // every first-of-month Board Report anchor one bucket too low,
      // leaving Sep/Oct/Nov synth positions uncovered AND stacked under
      // the wrongly-covered prior months. Local-time parsing fixes it.
      const cadenceType = task.cadenceType ?? "";
      const parseLocalYmd = (iso: string): Date | null => {
        const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!m) return null;
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      };
      const bucketIdxFromDate = (date: Date): number => {
        const m = date.getMonth();
        switch (cadenceType) {
          case "monthly_dom":
          case "monthly_dow":
            return m; // 0..11
          case "quarterly":
            return Math.floor(m / 3); // 0..3
          case "semiannual":
            return m < 6 ? 0 : 1; // 0..1
          case "annual":
          case "fiscal_year_end":
            return 0; // single position
          default:
            return -1; // weekly / biweekly / daily / school_term — use distance
        }
      };
      // Distance tolerance only used when bucketing returns -1 (weekly
      // family). Wider than half-gap so 1st-of-week anchors don't drift
      // past the boundary.
      const distTolerance = synthPositions.length === 1
        ? 1.0
        : 1.0 / synthPositions.length;

      // PASS 1 — real instances at actual positions with status visual.
      // Track which synthesized indices a real instance covers so the
      // base-layer pass below knows what to skip.
      const coveredSynthIdx = new Set<number>();
      for (const inst of taskInstances) {
        const pos = positionForIso(inst.periodAnchor);
        if (pos === null || pos < 0 || pos > 1) continue;
        const visual = statusVisualForInstance(inst, baseFill);
        dotsWithStatus.push({
          x: X_START + pos * X_RANGE,
          y: baseY,
          r: baseR,
          fill: visual.fill,
          stroke: visual.stroke,
          strokeWidth: visual.strokeWidth,
          opacity: visual.opacity,
          statusMark: visual.statusMark,
          label: labelForInstance(inst, task),
          task,
        });

        // Mark the covered synthesized index. Calendar bucket takes
        // precedence; falls back to closest-position when bucketing
        // doesn't apply to this cadence.
        const date = parseLocalYmd(inst.periodAnchor);
        const bucket = date === null ? -1 : bucketIdxFromDate(date);
        if (bucket >= 0 && bucket < synthPositions.length) {
          coveredSynthIdx.add(bucket);
        } else {
          let bestIdx = -1;
          let bestDist = distTolerance;
          for (let i = 0; i < synthPositions.length; i++) {
            const d = Math.abs(synthPositions[i] - pos);
            if (d <= bestDist) {
              bestDist = d;
              bestIdx = i;
            }
          }
          if (bestIdx >= 0) coveredSynthIdx.add(bestIdx);
        }
      }

      // PASS 2 — synthesized base layer for uncovered positions.
      for (let i = 0; i < synthPositions.length; i++) {
        if (coveredSynthIdx.has(i)) continue;
        const p = synthPositions[i];
        const isPast = p <= todayPosition;
        if (isPast) {
          // Past gap: no record exists for a position that should have
          // happened. Muted grey so the eye doesn't dwell, but still
          // visible enough to communicate "this should be here."
          dotsWithStatus.push({
            x: X_START + p * X_RANGE,
            y: baseY,
            r: baseR * 0.7,
            fill: "var(--text-muted)",
            opacity: 0.5,
            statusMark: null,
            label: cadName
              ? `${task.name} — ${cadName} (${monthForPosition(p)}) · No record`
              : `${task.name} (${monthForPosition(p)}) · No record`,
            task,
          });
        } else {
          // Future: upcoming work in full cadence-row color (the
          // original ribbon's default treatment).
          dotsWithStatus.push({
            x: X_START + p * X_RANGE,
            y: baseY,
            r: baseR,
            fill: baseFill,
            opacity: 1,
            statusMark: null,
            label: cadName
              ? `${task.name} — ${cadName} (${monthForPosition(p)})`
              : `${task.name} (${monthForPosition(p)})`,
            task,
          });
        }
      }
    }
  }

  // Map cadence type to color class for the card header.
  const cadenceColorClass = (cadenceType: string): string => {
    if (cadenceType === "weekly" || cadenceType === "biweekly" || cadenceType === "daily") return styles.cadBlue;
    if (cadenceType === "monthly_dom" || cadenceType === "monthly_dow") return styles.cadGreen;
    if (cadenceType === "quarterly" || cadenceType === "semiannual") return styles.cadAmber;
    if (cadenceType === "annual" || cadenceType === "fiscal_year_end" || cadenceType === "school_term") return styles.cadCoral;
    return styles.cadGrey;
  };

  return (
    <>
      {/* ════════════════════════════════════════════════════════════════
          DEV-1164 follow-up (2026-06-01) — Two parallel sections per
          Skip's feedback. The single-section layout that lumped standing
          duties under the "Recurring Duties" header read as sloppy
          because the section label mismatched its first content.

          Section 1: Standing Duties (own header, chip list)
          Section 2: Recurring Duties (rhythm name + year ribbon + cadence
                     stack)

          Class names `standingTail` / `standingLabel` are preserved for
          CSS continuity — the underlying structure changed but the
          chip-list visual stayed the same per the (A) treatment lock.
          ════════════════════════════════════════════════════════════════ */}

      {standing.length > 0 && (
        <div className={styles.detailSection}>
          <div className={styles.sectionLabel}>
            {t("engagements.rhythmStandingSectionLabel")}
            <span className={styles.sectionLabelSub}>
              {' · '}{standing.length}
            </span>
          </div>
          <div className={styles.standingTags}>
            {standing
              .sort(
                (a, b) =>
                  a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
              )
              .map((task) => (
                <button
                  type="button"
                  key={task.taskGuid}
                  className={styles.standingTag}
                  onClick={() => setTrayTask(task)}
                >
                  {task.name}
                </button>
              ))}
          </div>
        </div>
      )}

      <div className={styles.detailSection}>
        <div className={styles.sectionLabel}>
          {t("engagements.rhythmSectionLabel")}
        </div>
        <div className={styles.rhythmName}>{details.name}</div>

        {showRibbon && (
          <div className={styles.ribbonWrap}>
            <div className={styles.ribbonHead}>
              <span className={styles.ribbonLabel}>
                {t("engagements.rhythmYearAtAGlance")}
              </span>
              <span className={styles.ribbonHint}>
                {t("engagements.rhythmRibbonHint")}
              </span>
            </div>
            <svg
              viewBox="-2 -8 884 188"
              xmlns="http://www.w3.org/2000/svg"
              className={styles.ribbonSvg}
              role="img"
              aria-label={t("engagements.rhythmRibbonAria")}
            >
              {RIBBON_ROW_LABELS.map((label, i) => (
                <text
                  key={label}
                  x="20"
                  y={20 + i * 30}
                  fontSize="11"
                  fill="var(--text-muted)"
                >
                  {t(`engagements.rhythmRibbonRow.${label.toLowerCase()}`)}
                </text>
              ))}
              {[16, 46, 76, 106, 136].map((y) => (
                <line
                  key={y}
                  x1="80"
                  y1={y}
                  x2="860"
                  y2={y}
                  stroke="var(--border-default)"
                  strokeWidth="0.5"
                />
              ))}
              {/* DEV-1224 follow-up (2026-06-03): subtle vertical
                  month-boundary grid lines. 11 lines between the 12
                  months (Feb–Dec boundaries) so the grid frames the
                  cadence rows without overlapping the chart edges.
                  Same color/weight as horizontal rows so they read as
                  background grid; the today line stays the only
                  dashed/accent vertical. */}
              {Array.from({ length: 11 }, (_, i) => {
                const x = 80 + ((i + 1) / 12) * 780;
                return (
                  <line
                    key={`vmonth-${i}`}
                    x1={x}
                    y1={8}
                    x2={x}
                    y2={150}
                    stroke="var(--border-default)"
                    strokeWidth="0.5"
                    opacity="0.5"
                  />
                );
              })}
              {/* Month labels */}
              {["J","F","M","A","M","J","J","A","S","O","N","D"].map((m, i) => {
                const x = 80 + (i + 0.5) * (780 / 12);
                return (
                  <text
                    key={`m${i}`}
                    x={x}
                    y={168}
                    fontSize="11"
                    fill="var(--text-muted)"
                    textAnchor="middle"
                  >
                    {m}
                  </text>
                );
              })}
              {dotsWithStatus.map((d, idx) => (
                <g
                  key={idx}
                  opacity={d.opacity}
                  className={styles.ribbonDot}
                  onClick={() => setTrayTask(d.task)}
                  onMouseEnter={() => setHoveredDotIdx(idx)}
                  onMouseLeave={() => setHoveredDotIdx((i) => (i === idx ? null : i))}
                  onFocus={() => setHoveredDotIdx(idx)}
                  onBlur={() => setHoveredDotIdx((i) => (i === idx ? null : i))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setTrayTask(d.task);
                    }
                  }}
                  style={{ transformOrigin: `${d.x}px ${d.y}px` }}
                >
                  {/* DEV-1164 follow-up (2026-06-01): native SVG tooltip
                      so admin gets a free "what does this dot represent"
                      hover hint without bespoke event handling.
                      DEV-1224: tooltip now carries status + completer/
                      reason metadata when an instance is in hand.
                      DEV-1257-followup: <g> is now interactive — click
                      opens the task tray (same handler the list rows use),
                      hover magnifies via CSS so tight weekly dots stay
                      accessible. transformOrigin set inline because each
                      dot has its own center coordinates. */}
                  <title>{d.label}</title>
                  {/* DEV-1224 follow-up (2026-06-03): stroke+strokeWidth
                      support the hollow-ring treatment for completed
                      instances (fill=transparent, outlined in the
                      cadence-row color). Other states use solid fill
                      with no stroke. */}
                  <circle
                    cx={d.x}
                    cy={d.y}
                    r={d.r}
                    fill={d.fill}
                    stroke={d.stroke}
                    strokeWidth={d.strokeWidth}
                  />
                  {/* DEV-1224 — status mark overlay. Two glyphs only;
                      completed dots communicate via the hollow ring
                      shape itself (no glyph needed and tiny checks were
                      illegible at small sizes — Skip 2026-06-03 feedback). */}
                  {d.statusMark === "skipped" && (
                    <>
                      <line
                        x1={d.x - d.r * 0.5}
                        y1={d.y - d.r * 0.5}
                        x2={d.x + d.r * 0.5}
                        y2={d.y + d.r * 0.5}
                        stroke="#ffffff"
                        strokeWidth={Math.max(1, d.r * 0.3)}
                        strokeLinecap="round"
                      />
                      <line
                        x1={d.x - d.r * 0.5}
                        y1={d.y + d.r * 0.5}
                        x2={d.x + d.r * 0.5}
                        y2={d.y - d.r * 0.5}
                        stroke="#ffffff"
                        strokeWidth={Math.max(1, d.r * 0.3)}
                        strokeLinecap="round"
                      />
                    </>
                  )}
                  {d.statusMark === "overdue" && (
                    <circle
                      cx={d.x}
                      cy={d.y}
                      r={d.r + 1.5}
                      fill="none"
                      stroke="var(--color-error)"
                      strokeWidth="1.5"
                      opacity="0.7"
                    />
                  )}
                </g>
              ))}

              {/* DEV-1257-followup. Custom tooltip — instant on hover, themed,
                  legible 13px text. Renders inside the SVG via foreignObject
                  so it inherits the chart's coordinate system. Positioned
                  above the hovered dot, centered horizontally. The inner div
                  sizes to its content (fit-content) so long task names get
                  the room they need; the outer foreignObject is wide enough
                  to host the largest expected label. pointer-events:none on
                  the wrapper so it doesn't intercept mouse events meant for
                  the next dot when they overlap on hover. */}
              {hoveredDotIdx !== null && dotsWithStatus[hoveredDotIdx] && (() => {
                const d = dotsWithStatus[hoveredDotIdx];
                const TOOLTIP_W = 320;
                const TOOLTIP_H = 36;
                // Clamp horizontally so the tooltip doesn't render off-canvas
                // for dots near the left/right edges (Annual row + Weekly Dec).
                const x = Math.max(0, Math.min(940 - TOOLTIP_W, d.x - TOOLTIP_W / 2));
                // Place tooltip above the dot; for the bottom (Weekly) row
                // there's space above, for the top (Annual) row we flip below
                // so it doesn't get cut by the SVG top edge.
                const placeAbove = d.y > 30;
                const y = placeAbove ? d.y - (d.r + 6) - TOOLTIP_H : d.y + d.r + 6;
                return (
                  <foreignObject
                    x={x}
                    y={y}
                    width={TOOLTIP_W}
                    height={TOOLTIP_H}
                    style={{ pointerEvents: "none", overflow: "visible" }}
                  >
                    <div
                      style={{
                        background: "var(--bg-elevated)",
                        color: "var(--text-primary)",
                        padding: "5px 10px",
                        borderRadius: 6,
                        fontSize: 13,
                        lineHeight: 1.3,
                        border: "1px solid var(--border-emphasis)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                        width: "fit-content",
                        maxWidth: TOOLTIP_W - 4,
                        margin: "0 auto",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontFamily:
                          "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      {d.label}
                    </div>
                  </foreignObject>
                );
              })()}

              {/* DEV-1164 follow-up (2026-06-01): "today" vertical line
                  + small label. Spans the cadence rows (Annual at y=16
                  through Weekly at y=136). Skipped if for some reason
                  the position computation lands outside [0, 1]. */}
              {todayPosition >= 0 && todayPosition <= 1 && (
                <g>
                  <title>{t("engagements.rhythmRibbonToday")}</title>
                  <line
                    x1={80 + todayPosition * 780}
                    y1={8}
                    x2={80 + todayPosition * 780}
                    y2={150}
                    stroke="var(--accent-primary)"
                    strokeWidth="1.5"
                    strokeDasharray="3,3"
                    opacity="0.85"
                  />
                  <text
                    x={80 + todayPosition * 780}
                    y={6}
                    fontSize="10"
                    fill="var(--accent-primary)"
                    textAnchor="middle"
                    fontWeight="600"
                  >
                    {t("engagements.rhythmRibbonToday")}
                  </text>
                </g>
              )}
            </svg>
          </div>
        )}

        {orderedCadenceKeys.length > 0 && (
          <div className={styles.cadenceStack}>
            {orderedCadenceKeys.map((cadence) => {
              const tasks = (byCadence.get(cadence) ?? []).sort(
                (a, b) =>
                  a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
              );
              return (
                <div key={cadence} className={styles.cadenceCard}>
                  <div className={styles.cadenceCardHead}>
                    <span
                      className={`${styles.cadenceCardCad} ${cadenceColorClass(cadence)}`}
                    >
                      {cadenceLabel(cadence)}
                    </span>
                    <span className={styles.cadenceCardCount}>
                      {tasks.length === 1
                        ? t("engagements.rhythmDutyCountOne")
                        : t("engagements.rhythmDutyCountN").replace(
                            "{{n}}",
                            String(tasks.length),
                          )}
                    </span>
                  </div>
                  {tasks.map((task) => {
                    // DEV-1233 follow-up (2026-06-04): show the human
                    // anchor text ("15th of each month", "Dec 31") beside
                    // the task name so users see WHEN it fires, not just
                    // that it's "monthly."
                    const anchorText = formatCadenceAnchor(
                      task.cadenceType ?? "",
                      task.cadenceAnchor,
                    );
                    return (
                      <button
                        type="button"
                        key={task.taskGuid}
                        className={styles.cadenceTask}
                        onClick={() => setTrayTask(task)}
                      >
                        <span className={styles.cadName}>
                          {task.name}
                          {anchorText && (
                            <span className={styles.cadAnchor}>
                              {" · "}{anchorText}
                            </span>
                          )}
                        </span>
                        <span className={styles.cadLead}>
                          {t("engagements.rhythmLeadTimeShort").replace(
                            "{{n}}",
                            String(task.leadTimeDays),
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

      </div>

      {trayTask && (
        <RhythmTaskTray task={trayTask} onClose={() => setTrayTask(null)} />
      )}
    </>
  );
}

export default RhythmDisplayPanel;
